import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { formatDate } from '@/lib/date';
import { downloadPublicFile } from '@/lib/fileDownload';
import StatusBadge from '@/components/base/StatusBadge';
import Modal from '@/components/base/Modal';
import { getStatusMeta } from '@/lib/statusMap';
import DocumentModal from './components/DocumentModal';

interface DocRow {
  id: string;
  title: string;
  description: string | null;
  type: string | null;
  lesson_id: string | null;
  group_id: string | null;
  level: string | null;
  published: boolean;
  downloadable: boolean;
  upload_date: string | null;
  downloads: number;
  size: string | null;
  file_url: string | null;
  created_at: string;
}

interface LessonInfo {
  id: string;
  title: string;
  group_id: string | null;
  level: string | null;
}

interface GroupInfo {
  id: string;
  name: string;
  color: string | null;
  level: string | null;
}

export default function Dokumanlar() {
  const [docs, setDocs] = useState<DocRow[]>([]);
  const [lessons, setLessons] = useState<LessonInfo[]>([]);
  const [groups, setGroups] = useState<GroupInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<string>('hepsi');
  const [modalOpen, setModalOpen] = useState(false);
  const [editDoc, setEditDoc] = useState<DocRow | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<DocRow | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [
        { data: docsData, error: docsError },
        { data: lessonsData, error: lessonsError },
        { data: groupsData, error: groupsError },
      ] = await Promise.all([
        supabase.from('zehrahoca_documents').select('*').order('upload_date', { ascending: false }),
        supabase.from('zehrahoca_lessons').select('id, title, group_id, level').order('date', { ascending: false }),
        supabase.from('zehrahoca_groups').select('id, name, color, level').eq('active', true),
      ]);
      if (docsError) throw docsError;
      if (lessonsError) throw lessonsError;
      if (groupsError) throw groupsError;
      setDocs((docsData ?? []) as DocRow[]);
      setLessons((lessonsData ?? []) as LessonInfo[]);
      setGroups((groupsData ?? []) as GroupInfo[]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const handleChange = () => fetchAll();
    window.addEventListener('zehrahoca:docs-changed', handleChange);
    return () => window.removeEventListener('zehrahoca:docs-changed', handleChange);
  }, [fetchAll]);

  const lessonMap = useMemo(() => {
    const m: Record<string, LessonInfo> = {};
    lessons.forEach((l) => { m[l.id] = l; });
    return m;
  }, [lessons]);

  const groupMap = useMemo(() => {
    const m: Record<string, GroupInfo> = {};
    groups.forEach((g) => { m[g.id] = g; });
    return m;
  }, [groups]);

  const typeMeta: Record<string, { label: string; color: string; icon: string }> = {
    pdf: { label: 'PDF', color: 'red', icon: 'ri-file-pdf-line' },
    video: { label: 'Video', color: 'emerald', icon: 'ri-video-line' },
    sunum: { label: 'Sunum', color: 'amber', icon: 'ri-slideshow-line' },
    word: { label: 'Word', color: 'blue', icon: 'ri-file-word-line' },
    excel: { label: 'Excel', color: 'emerald', icon: 'ri-file-excel-line' },
    diger: { label: 'Diğer', color: 'gray', icon: 'ri-file-line' },
  };

  const filtered = useMemo(() => {
    let list = docs;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((d) =>
        (d.title ?? '').toLowerCase().includes(q) ||
        (d.description ?? '').toLowerCase().includes(q),
      );
    }
    if (filterType !== 'hepsi') {
      list = list.filter((d) => (d.type ?? 'diger') === filterType);
    }
    return list;
  }, [docs, search, filterType]);

  async function doDelete() {
    if (!confirmDelete) return;
    try {
      const { error: delError } = await supabase.from('zehrahoca_documents').delete().eq('id', confirmDelete.id);
      if (delError) throw delError;
      setConfirmDelete(null);
      window.dispatchEvent(new Event('zehrahoca:docs-changed'));
    } catch (err) {
      setError((err as Error).message || 'Silme işlemi başarısız oldu.');
      setConfirmDelete(null);
    }
  }

  function handleEdit(d: DocRow) {
    setEditDoc(d);
    setModalOpen(true);
  }

  function handleNew() {
    setEditDoc(null);
    setModalOpen(true);
  }

  if (loading) {
    return (
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-center py-20">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-500" />
        <p className="mt-4 text-sm text-foreground-500">Dokümanlar yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-7xl py-10">
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
          <i className="ri-error-warning-line text-2xl text-red-500" />
          <p className="mt-2 text-sm font-medium text-red-700">Dokümanlar yüklenirken bir hata oluştu.</p>
          <p className="mt-1 text-xs text-red-500">{error}</p>
          <button type="button" onClick={fetchAll} className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-600 cursor-pointer">
            <i className="ri-refresh-line" /> Yeniden Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">Dokümanlar</h1>
          <p className="mt-1 text-sm text-foreground-600">PDF, sunum ve diğer dosyaları yönetin ve öğrencilerle paylaşın.</p>
        </div>
        <button type="button" onClick={handleNew} className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-600 cursor-pointer">
          <i className="ri-add-line text-base" /> Yeni Doküman Ekle
        </button>
      </div>

      {/* Filtreler */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Doküman ara..."
            className="w-full rounded-lg border border-background-300 bg-white py-2.5 pl-9 pr-3 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
        >
          <option value="hepsi">Tüm Tipler</option>
          <option value="pdf">PDF</option>
          <option value="video">Video</option>
          <option value="sunum">Sunum</option>
          <option value="word">Word</option>
          <option value="excel">Excel</option>
          <option value="diger">Diğer</option>
        </select>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.length === 0 ? (
          <div className="col-span-full rounded-xl border border-background-200 bg-white p-10 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-line text-2xl text-foreground-400" />
            </div>
            <p className="text-sm font-semibold text-foreground-700">Doküman bulunamadı.</p>
          </div>
        ) : (
          filtered.map((d) => {
            const t = typeMeta[d.type ?? 'diger'] ?? typeMeta.diger;
            const group = groupMap[d.group_id ?? ''];
            const publishedMeta = getStatusMeta(
              d.published ? 'yayinda' : 'taslak',
              { yayinda: { label: 'Yayında', color: 'emerald' }, taslak: { label: 'Taslak', color: 'amber' } },
            );
            return (
              <div
                key={d.id}
                className="group relative flex flex-col rounded-xl border border-background-200 bg-white p-5 transition hover:shadow-md"
              >
                <div className="mb-3 flex items-start justify-between">
                  <div className={`flex h-10 w-10 items-center justify-center rounded-lg`} style={{ backgroundColor: t.color === 'red' ? '#fee2e2' : t.color === 'emerald' ? '#d1fae5' : t.color === 'amber' ? '#fef3c7' : t.color === 'blue' ? '#dbeafe' : '#f3f4f6', color: t.color === 'red' ? '#ef4444' : t.color === 'emerald' ? '#10b981' : t.color === 'amber' ? '#f59e0b' : t.color === 'blue' ? '#3b82f6' : '#9ca3af' }}>
                    <i className={`${t.icon} text-xl`} />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <StatusBadge meta={publishedMeta} />
                    {d.downloadable && (
                      <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-600">
                        <i className="ri-download-line" /> İndirilebilir
                      </span>
                    )}
                  </div>
                </div>

                <h3 className="font-semibold text-foreground-950 text-sm leading-snug mb-1">{d.title}</h3>
                {d.description && <p className="text-xs text-foreground-600 line-clamp-2 mb-3">{d.description}</p>}

                <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
                  {d.lesson_id && lessonMap[d.lesson_id] ? (
                    <span className="inline-flex items-center gap-1 rounded-md bg-primary-50 px-2 py-0.5 font-semibold text-primary-700">
                      <i className="ri-book-open-line" /> {lessonMap[d.lesson_id].title}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-md bg-background-100 px-2 py-0.5 font-semibold text-foreground-500">
                      <i className="ri-file-line" /> Genel doküman
                    </span>
                  )}
                  {group && (
                    <span className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 font-semibold" style={{ backgroundColor: `${group.color ?? '#94a3b8'}20`, color: group.color ?? '#94a3b8' }}>
                      <i className="ri-team-line" /> {group.name}
                    </span>
                  )}
                  {d.level && (
                    <span className="inline-flex items-center gap-1 rounded-md bg-background-100 px-2 py-0.5 font-semibold">
                      <i className="ri-bar-chart-line" /> {d.level}
                    </span>
                  )}
                  {d.size && <span>{d.size}</span>}
                </div>

                <div className="mt-auto flex items-center justify-between border-t border-background-100 pt-3">
                  <span className="text-xs text-foreground-400">
                    {d.upload_date ? formatDate(d.upload_date) : '—'} · {d.downloads} indirme
                  </span>
                  <div className="flex items-center gap-1.5">
                    {d.file_url && (
                      <>
                        <a
                          href={d.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-600 transition hover:bg-primary-100"
                          title="Aç"
                        >
                          <i className="ri-external-link-line text-base" />
                        </a>
                        <button
                          type="button"
                          onClick={() => downloadPublicFile(d.file_url, d.title)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-600 transition hover:bg-primary-100"
                          title="İndir"
                        >
                          <i className="ri-download-line text-base" />
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={() => handleEdit(d)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-background-100 text-foreground-600 transition hover:bg-background-200"
                      title="Düzenle"
                    >
                      <i className="ri-edit-line text-base" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(d)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 text-red-500 transition hover:bg-red-100"
                      title="Sil"
                    >
                      <i className="ri-delete-bin-line text-base" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <DocumentModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        doc={editDoc}
        lessons={lessons}
        groups={groups}
      />

      <Modal
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title="Silmek istediğinize emin misiniz?"
        subtitle="Bu işlem ilgili kaydı panelden kaldırır. Bağlantılı içerikler etkilenebilir."
      >
        <div className="space-y-5">
          <p className="text-sm text-foreground-700">
            <span className="font-semibold">{confirmDelete?.title}</span> dokümanı kalıcı olarak silinecek.
          </p>
          <div className="flex items-center justify-end gap-3 border-t border-background-200 pt-5">
            <button
              type="button"
              onClick={() => setConfirmDelete(null)}
              className="rounded-lg px-4 py-2.5 text-sm font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer whitespace-nowrap"
            >
              Vazgeç
            </button>
            <button
              type="button"
              onClick={doDelete}
              className="rounded-lg bg-red-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-red-600 cursor-pointer whitespace-nowrap"
            >
              Sil
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}